/**
 * Vital Socks: core interactions (no dependencies).
 * Header state, menu + cart drawers, AJAX cart (Section Rendering API),
 * product form, gallery, fit finder, sorting and product recommendations.
 * Scroll animation lives separately in motion.js.
 */
(() => {
  const VS = (window.VS = window.VS || {});
  const routes = VS.routes || {};
  const strings = VS.strings || {};
  const $ = (sel, root = document) => root.querySelector(sel);
  const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));

  /* ---------------------------------------------------------------------
     Overlays (menu drawer, cart drawer): open/close, focus trap, Escape
     --------------------------------------------------------------------- */
  const FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
  let active = null;

  function openOverlay(root, trigger) {
    if (!root) return;
    if (active && active.root !== root) closeOverlay({ restoreFocus: false });
    active = { root, trigger: trigger || document.activeElement };
    root.classList.add('is-open');
    document.body.classList.add('has-overlay-open');
    VS.lenis?.stop();
    if (trigger?.hasAttribute('aria-expanded')) trigger.setAttribute('aria-expanded', 'true');
    const panel = $('[role="dialog"]', root);
    window.requestAnimationFrame(() => panel?.focus());
  }

  function closeOverlay({ restoreFocus = true } = {}) {
    if (!active) return;
    const { root, trigger } = active;
    root.classList.remove('is-open');
    document.body.classList.remove('has-overlay-open');
    VS.lenis?.start();
    if (trigger?.hasAttribute('aria-expanded')) trigger.setAttribute('aria-expanded', 'false');
    active = null;
    if (restoreFocus && trigger && document.contains(trigger)) trigger.focus();
  }

  document.addEventListener('keydown', (event) => {
    if (!active) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      closeOverlay();
      return;
    }
    if (event.key !== 'Tab') return;
    const panel = $('[role="dialog"]', active.root);
    const items = $$(FOCUSABLE, panel).filter((el) => el.offsetParent !== null);
    if (!items.length) return;
    const first = items[0];
    const last = items[items.length - 1];
    if (event.shiftKey && (document.activeElement === first || document.activeElement === panel)) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  });

  /* Keep --announce-h exact so the hero fills the screen below the bar. */
  const announcement = $('.announcement');
  const setAnnounce = () => {
    if (announcement) {
      document.documentElement.style.setProperty('--announce-h', `${announcement.offsetHeight}px`);
    }
  };
  setAnnounce();
  window.addEventListener('resize', setAnnounce);

  /* ---------------------------------------------------------------------
     Header: solid once scrolled (overlay mode waits until the hero passes)
     --------------------------------------------------------------------- */
  const header = $('[data-header]');
  if (header) {
    const isOverlay = header.hasAttribute('data-overlay');
    let threshold = 4;
    // Overlay mode stays transparent only for the first stretch of the hero,
    // so hero copy never scrolls under a see-through header.
    const measure = () => {
      threshold = isOverlay ? 60 : 4;
    };
    let ticking = false;
    const update = () => {
      header.classList.toggle('is-scrolled', window.scrollY > threshold);
      ticking = false;
    };
    measure();
    update();
    window.addEventListener(
      'scroll',
      () => {
        if (!ticking) {
          ticking = true;
          window.requestAnimationFrame(update);
        }
      },
      { passive: true }
    );
    window.addEventListener('resize', () => {
      measure();
      update();
    });
  }

  /* ---------------------------------------------------------------------
     Menu drawer
     --------------------------------------------------------------------- */
  const menuDrawer = $('[data-menu-drawer]');
  document.addEventListener('click', (event) => {
    const openBtn = event.target.closest('[data-menu-open]');
    if (openBtn && menuDrawer) {
      openOverlay(menuDrawer, openBtn);
      return;
    }
    if (event.target.closest('[data-menu-close]')) closeOverlay();
    if (active?.root === menuDrawer && event.target.closest('.menu-drawer__list a')) closeOverlay({ restoreFocus: false });
  });

  /* ---------------------------------------------------------------------
     Cart
     --------------------------------------------------------------------- */
  const cart = {
    get drawer() {
      return $('[data-cart-drawer]');
    },

    setCount(count) {
      $$('[data-cart-count]').forEach((el) => (el.textContent = count > 0 ? count : ''));
    },

    render(sections) {
      const html = sections && sections['cart-drawer'];
      if (!html) return;
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const fresh = doc.querySelector('#CartDrawerContents');
      const current = $('#CartDrawerContents');
      const hadFocus = current && current.contains(document.activeElement);
      if (fresh && current) current.replaceWith(fresh);
      if (fresh) this.setCount(Number(fresh.dataset.itemCount || 0));
      if (fresh) setDispatch(fresh);
      if (fresh) syncCartEmail(fresh);
      // Focus was inside the replaced markup: keep it in the open drawer.
      if (hadFocus && active && active.root === this.drawer) $('[role="dialog"]', this.drawer)?.focus();
    },

    async post(url, body, isForm = false) {
      let response;
      try {
        response = await fetch(url, {
          method: 'POST',
          headers: isForm
            ? { Accept: 'application/json' }
            : { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: isForm ? body : JSON.stringify(body),
        });
      } catch (e) {
        // Offline or the request never landed: say so plainly, not "Failed to fetch".
        throw new Error(strings.cartError);
      }
      const data = await response.json().catch(() => ({}));
      if (!response.ok || data.status) {
        const error = new Error(data.description || data.message || strings.cartError);
        error.data = data;
        throw error;
      }
      return data;
    },

    async add(formData) {
      formData.append('sections', 'cart-drawer');
      formData.append('sections_url', window.location.pathname);
      const data = await this.post(`${routes.cartAdd}.js`, formData, true);
      this.render(data.sections);
      return data;
    },

    // Several variants in one request, e.g. a Trio in M, M and L:
    // [{ id: M, quantity: 2 }, { id: L, quantity: 1 }]. When a line asks for
    // more than is in stock, Shopify adds what it has and still answers 422,
    // so callers refresh the drawer in their catch (see refresh below).
    async addItems(items) {
      const data = await this.post(`${routes.cartAdd}.js`, {
        items,
        sections: 'cart-drawer',
        sections_url: window.location.pathname,
      });
      this.render(data.sections);
      return data;
    },

    // Redraw the drawer and count from the real cart. Used after a failed add,
    // because a 422 for stock can follow a partial add (checked 30 Oct 2026).
    async refresh() {
      const response = await fetch(`${window.location.pathname}?sections=cart-drawer`);
      if (response.ok) this.render(await response.json());
    },

    async change(key, quantity) {
      const data = await this.post(`${routes.cartChange}.js`, {
        id: key,
        quantity,
        sections: 'cart-drawer',
        sections_url: window.location.pathname,
      });
      this.render(data.sections);
      return data;
    },

    open(trigger) {
      if (VS.cartType === 'drawer' && this.drawer) openOverlay(this.drawer, trigger);
      else window.location.href = routes.cart;
    },
  };
  VS.cart = cart;

  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-cart-open]');
    if (opener && VS.cartType === 'drawer' && cart.drawer) {
      event.preventDefault();
      cart.open(opener);
      return;
    }
    if (event.target.closest('[data-cart-close]')) {
      closeOverlay();
      return;
    }
    const line = event.target.closest('[data-line-key]');
    if (!line) return;
    const key = line.dataset.lineKey;
    const input = $('[data-qty-input]', line);
    const step = event.target.closest('[data-qty-change]');
    if (step && input) {
      const next = Math.max(0, Number(input.value || 0) + Number(step.dataset.qtyChange));
      line.setAttribute('aria-busy', 'true');
      cart.change(key, next).catch(() => line.removeAttribute('aria-busy'));
    }
    if (event.target.closest('[data-line-remove]')) {
      line.setAttribute('aria-busy', 'true');
      cart.change(key, 0).catch(() => line.removeAttribute('aria-busy'));
    }
  });

  document.addEventListener('change', (event) => {
    const input = event.target.closest('[data-qty-input]');
    if (!input) return;
    const line = input.closest('[data-line-key]');
    if (line) cart.change(line.dataset.lineKey, Math.max(0, Number(input.value || 0)));
  });

  /* ---------------------------------------------------------------------
     Email sign-up in the cart (snippets/cart-email)
     Shopify's customer form needs a spam-check token. Shopify's hCaptcha
     script binds to the form on first focus, takes over its submit, fetches
     the token, adds it to the form, then calls the form.submit it found when
     it bound. So the form gets its own submit first (prepareCartEmail), which
     posts in the background and keeps the visitor in the cart. If Shopify ever
     changes that script, the form simply posts normally and return_to brings
     the visitor back to the cart page, as it does without JavaScript. The
     drawer redraws on every cart change, so the result is kept: this browser
     session keeps the "check your email" line, later visits don't ask again.
     --------------------------------------------------------------------- */
  const CART_EMAIL_KEY = 'vs-cart-email';
  let cartEmailDone = false; // Also covers a browser that blocks storage.
  const cartEmailStore = (kind, write) => {
    try {
      const store = window[kind];
      if (write) store.setItem(CART_EMAIL_KEY, '1');
      return store.getItem(CART_EMAIL_KEY) === '1';
    } catch (e) {
      return false; // Storage blocked: the form simply shows again.
    }
  };
  const cartEmailBusy = (form, busy) => {
    const button = $('button[type="submit"]', form);
    if (button) button.disabled = busy;
    if (busy) form.setAttribute('aria-busy', 'true');
    else form.removeAttribute('aria-busy');
    window.clearTimeout(form.cartEmailTimer);
    // The spam check can fail or be closed without a word: let them try again.
    if (busy) form.cartEmailTimer = window.setTimeout(() => cartEmailBusy(form, false), 8000);
  };
  const postCartEmail = async (form) => {
    if (form.dataset.posting) return;
    form.dataset.posting = 'true';
    const error = $('[data-cart-email-error]', form);
    let response;
    let html = '';
    try {
      // Without return_to, Shopify answers with this page and customer_posted=true.
      const body = new URLSearchParams(new FormData(form));
      body.delete('return_to');
      response = await fetch(form.getAttribute('action').split('#')[0], { method: 'POST', body });
      if (!response.url.includes('customer_posted=true')) html = await response.text();
    } catch (e) {
      response = null;
    }
    delete form.dataset.posting;
    // Cloudflare or Shopify asking the visitor to prove they're human: that
    // needs the whole page, so post normally. return_to comes back to the cart.
    if (response && (response.status === 403 || response.headers.get('cf-mitigated') || response.url.includes('/challenge'))) {
      HTMLFormElement.prototype.submit.call(form);
      return;
    }
    cartEmailBusy(form, false);
    // Shopify answers with the page it sends the visitor back to.
    if (!response || !response.ok || !(response.url.includes('customer_posted=true') || html.includes('data-cart-email-posted'))) {
      if (error) error.hidden = false;
      return;
    }
    cartEmailDone = true;
    cartEmailStore('sessionStorage', true);
    cartEmailStore('localStorage', true);
    const box = form.closest('[data-cart-email]');
    syncCartEmail();
    $('[data-cart-email-done]', box)?.focus();
  };
  // A remembered sign-up shows the line in the same session, nothing after that.
  const syncCartEmail = (root = document) => {
    $$('[data-cart-email-form]', root).forEach((form) => {
      if (!form.dataset.hcaptchaBound && !form.dataset.recaptchaBound) form.submit = () => postCartEmail(form);
    });
    if ($('[data-cart-email-posted]', root)) {
      cartEmailDone = true;
      cartEmailStore('sessionStorage', true);
      cartEmailStore('localStorage', true);
    }
    const now = cartEmailDone || cartEmailStore('sessionStorage');
    if (!now && !cartEmailStore('localStorage')) return;
    $$('[data-cart-email]', root).forEach((box) => {
      box.hidden = !now;
      const form = $('form', box);
      const done = $('[data-cart-email-done]', box);
      if (form) form.hidden = true;
      if (done) done.hidden = false;
    });
  };
  // In the drawer's short scrolling part, bring the whole offer into view when
  // the field opens, so the small print shows while the shopper presses the button.
  document.addEventListener('focusin', (event) => {
    const box = event.target.closest('[data-cart-email]');
    const body = box?.closest('.drawer__body');
    if (body && event.target.matches('input')) {
      body.scrollTop += box.getBoundingClientRect().top - body.getBoundingClientRect().top;
    }
  });
  document.addEventListener('submit', (event) => {
    const form = event.target.closest('[data-cart-email-form]');
    if (!form) return;
    event.preventDefault();
    if (form.hasAttribute('aria-busy')) return;
    const error = $('[data-cart-email-error]', form);
    if (error) error.hidden = true;
    cartEmailBusy(form, true);
    // Once bound, Shopify's script already has this submit in hand.
    const bound = () => form.dataset.hcaptchaBound || form.dataset.recaptchaBound;
    if (bound()) return;
    // Pressed before the script finished loading after the first focus: wait
    // for it, then submit again. With no script to wait for, post as it is.
    const started = Date.now();
    const waitForBind = () => {
      if (bound()) {
        form.submit();
      } else if (!window.Shopify?.captcha || Date.now() - started > 6000) {
        postCartEmail(form);
      } else {
        window.setTimeout(waitForBind, 150);
      }
    };
    waitForBind();
  });

  /* ---------------------------------------------------------------------
     The day an order goes out (snippets/dispatch-line, under Add to cart
     and in the cart): the next working day after today in South African
     time, Monday to Friday, skipping the dates in Theme settings > Sending
     orders. So Monday to Thursday orders go the next day; Friday, Saturday
     and Sunday orders go on Monday. "[day]" in the line becomes the weekday,
     or the date ("4 January") when a holiday or closure puts it more than
     three days away. If the day can't be worked out, the line without the
     day stays.
     --------------------------------------------------------------------- */
  const DAY_MS = 86400000;
  const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

  // Today's date in Johannesburg, as midnight UTC of that date.
  function saToday() {
    const now = new Date(Date.now());
    try {
      const parts = new Intl.DateTimeFormat('en-GB', {
        timeZone: 'Africa/Johannesburg',
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
      }).formatToParts(now);
      const part = (type) => Number(parts.find((p) => p.type === type).value);
      const today = Date.UTC(part('year'), part('month') - 1, part('day'));
      if (!Number.isNaN(today)) return today;
    } catch (error) {
      // Older browsers without time zones: South Africa is UTC+2 all year.
    }
    const sa = new Date(now.getTime() + 2 * 3600000);
    return Date.UTC(sa.getUTCFullYear(), sa.getUTCMonth(), sa.getUTCDate());
  }

  // "2026-12-25 Christmas Day" or "2026-12-28 to 2027-01-03 Holiday closure", one per line.
  function closedDays(text) {
    const days = new Set();
    String(text || '')
      .split('\n')
      .forEach((line) => {
        const m = line.match(/^\s*(\d{4})-(\d{2})-(\d{2})(?:\s+to\s+(\d{4})-(\d{2})-(\d{2}))?/);
        if (!m) return;
        const from = Date.UTC(+m[1], +m[2] - 1, +m[3]);
        const to = m[4] ? Date.UTC(+m[4], +m[5] - 1, +m[6]) : from;
        for (let day = from, n = 0; day <= to && n < 62; day += DAY_MS, n += 1) days.add(day);
      });
    return days;
  }

  function sendDay(today, closed) {
    for (let day = today + DAY_MS, n = 0; n < 62; day += DAY_MS, n += 1) {
      const weekday = new Date(day).getUTCDay();
      if (weekday !== 0 && weekday !== 6 && !closed.has(day)) return day;
    }
    return null;
  }

  function setDispatch(root = document) {
    const lines = $$('[data-dispatch]', root);
    const template = VS.dispatch && VS.dispatch.text;
    if (!lines.length || !template || !template.includes('[day]')) return;
    const today = saToday();
    const send = sendDay(today, closedDays(VS.dispatch.closed));
    if (send === null) return;
    const date = new Date(send);
    // A weekday reads clearly within the next few days; further off, the date.
    const day = send - today > 3 * DAY_MS ? `${date.getUTCDate()} ${MONTHS[date.getUTCMonth()]}` : WEEKDAYS[date.getUTCDay()];
    const text = template.replace('[day]', day);
    lines.forEach((line) => {
      const label = $('[data-dispatch-label]', line);
      if (label && label.textContent !== text) label.textContent = text;
      line.hidden = false;
    });
  }

  /* ---------------------------------------------------------------------
     Product page: variants, gallery, quantity, add to cart
     --------------------------------------------------------------------- */
  function initProduct(section) {
    if (section.dataset.ready) return;
    section.dataset.ready = 'true';
    const data = JSON.parse($('[data-product-json]', section)?.textContent || '{"variants":[]}');
    const variants = data.variants || [];
    const form = $('form[data-product-form]', section) || $('form', section);
    const idInput = $('[data-variant-id]', section);
    const addBtn = $('[data-add-button]', section);
    const addLabel = $('[data-add-label]', section);
    const priceNow = $('[data-price-current]', section);
    const priceWas = $('[data-price-compare]', section);
    const errorBox = $('[data-form-error]', section);
    const fieldsets = $$('[data-option-index]', section);

    const selectedOptions = () => fieldsets.map((fs) => $('input:checked', fs)?.value);

    // Bundle picker: the button shows the total for the chosen number of pairs.
    // The radios (bundle_quantity) only choose; the form's one "quantity" field
    // is [data-bundle-qty], kept on the picked pack, because Shopify's Buy it now
    // reads the first field named quantity (it used to get the Single radio, 1).
    const bundleInputs = $$('[data-bundle] input[data-bundle-option]', section);
    const bundleQty = $('[data-bundle] [data-bundle-qty]', section);
    const syncQty = () => {
      const picked = bundleInputs.find((input) => input.checked);
      if (bundleQty && picked && bundleQty.value !== picked.value) bundleQty.value = picked.value;
    };
    const addTotal = $('[data-add-total]', section);
    const formatMoney = (cents) => {
      const amount = (cents / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      const format = VS.moneyFormat || 'R {{amount}}';
      return format.replace(/\{\{\s*amount\w*\s*\}\}/, amount);
    };
    const updateTotal = (variant) => {
      if (!addTotal || !bundleInputs.length) return;
      const picked = bundleInputs.find((input) => input.checked) || bundleInputs[0];
      const fixed = picked.dataset.bundleFixed === 'true';
      const unit = fixed ? Number(picked.dataset.bundleUnit) : Number(variant?.price_cents ?? picked.dataset.bundleUnit);
      addTotal.textContent = `· ${formatMoney(unit * Number(picked.value))}`;
    };
    const currentVariant = () => variants.find((v) => String(v.id) === String(idInput?.value)) || variants[0];
    bundleInputs.forEach((input) =>
      input.addEventListener('change', () => {
        syncQty();
        updateTotal(currentVariant());
      })
    );
    syncQty();
    updateTotal(currentVariant());
    // Back/forward cache: the browser may restore the picked radio, so match it again.
    window.addEventListener('pageshow', () => {
      syncQty();
      updateTotal(currentVariant());
    });
    // Belt and braces: match again just before any button in the form acts,
    // including Shopify's Buy it now (capture runs before its own handler).
    form?.addEventListener('click', syncQty, true);

    // Size for each pair (snippets/bundle-picker): with Duo, Trio or Five-pack
    // picked, one row of size chips per pair. Every pair starts on the size
    // picked above; pairs nobody has touched follow that size when it
    // changes, and while every pair has the same size they all do (it's then
    // one size for the pack). When the pairs end up all one size, the size
    // above moves to it, so the form's id is right for Shopify's Buy it now.
    // If every pair has the size above, nothing changes: one variant, the
    // pack's quantity, as before. If not ("mixed"), Add to cart and the
    // phone bar post every size as its own line (cart.addItems), and the
    // theme's Buy it now replaces Shopify's, which can only buy one variant.
    const pairBox = $('[data-pair-sizes]', section);
    const pairRows = pairBox ? $$('[data-pair-row]', pairBox) : [];
    const sizeRadios = fieldsets[0] ? $$('input[data-option]', fieldsets[0]) : [];
    const dynamic = $('[data-dynamic-checkout]', section);
    const buyNow = $('[data-buy-now]', section);
    const mainSize = () => sizeRadios.find((input) => input.checked)?.value;
    const pairs = pairRows.map(() => ({ size: mainSize(), touched: false }));
    const sizeVariant = (size) => variants.find((v) => v.options[0] === size);
    // How many rows show: the picked pack, from 2 pairs up (Single has none).
    const pairCount = () => {
      const qty = Number(bundleInputs.find((input) => input.checked)?.value || 1);
      return qty > 1 && qty <= pairRows.length ? qty : 0;
    };
    const shownPairs = () => pairs.slice(0, pairCount());
    const isMixed = () => shownPairs().some((pair) => pair.size !== mainSize());
    // The lines to post when mixed: one per size, in pair order. Null otherwise.
    const mixedItems = () => {
      if (!isMixed()) return null;
      const items = [];
      shownPairs().forEach((pair) => {
        const variant = sizeVariant(pair.size);
        if (!variant) return;
        const line = items.find((item) => item.id === variant.id);
        if (line) line.quantity += 1;
        else items.push({ id: variant.id, quantity: 1 });
      });
      return items;
    };
    const renderPairs = () => {
      if (!pairBox) return;
      const count = pairCount();
      pairBox.hidden = count === 0;
      pairRows.forEach((row, index) => {
        row.hidden = index >= count;
        $$('input[data-pair-size]', row).forEach((input) => {
          const ok = !!sizeVariant(input.value)?.available;
          input.disabled = !ok; // sold out: shown struck through, can't be picked
          input.nextElementSibling?.classList.toggle('is-unavailable', !ok);
          input.checked = input.value === pairs[index].size;
        });
      });
      const mixed = isMixed();
      const allAvailable = shownPairs().every((pair) => sizeVariant(pair.size)?.available);
      if (addBtn && !addBtn.hasAttribute('aria-busy')) {
        const available = mixed ? allAvailable : !!currentVariant()?.available;
        addBtn.disabled = !available;
        if (addLabel) addLabel.textContent = available ? strings.addToCart : strings.soldOut;
      }
      dynamic?.classList.toggle('is-mixed', mixed && !!buyNow);
      if (buyNow) {
        buyNow.hidden = !mixed;
        if (!buyNow.hasAttribute('aria-busy')) buyNow.disabled = !allAvailable;
      }
      section.dispatchEvent(new CustomEvent('vs:pairs'));
    };
    // The pairs all one size, but not the size above: move the size above.
    const matchMainSize = () => {
      const sizes = shownPairs().map((pair) => pair.size);
      if (!sizes.length || sizes.some((size) => size !== sizes[0]) || sizes[0] === mainSize()) return;
      const input = sizeRadios.find((radio) => radio.value === sizes[0]);
      if (!input) return;
      input.checked = true;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    };
    if (pairBox) {
      // Registered before the size picker's own listener (update), so the
      // rows have followed by the time update draws them.
      fieldsets[0].addEventListener('change', () => {
        const size = mainSize();
        const sizes = shownPairs().map((pair) => pair.size);
        const oneSize = sizes.every((s) => s === sizes[0]);
        pairs.forEach((pair) => {
          if (oneSize || !pair.touched) {
            pair.size = size;
            pair.touched = false;
          }
        });
      });
      pairBox.addEventListener('change', (event) => {
        const input = event.target.closest('input[data-pair-size]');
        const index = pairRows.indexOf(input?.closest('[data-pair-row]'));
        if (index < 0) return;
        pairs[index] = { size: input.value, touched: true };
        matchMainSize();
        renderPairs();
      });
      bundleInputs.forEach((input) =>
        input.addEventListener('change', () => {
          matchMainSize();
          renderPairs();
        })
      );
      // Back/forward: the browser may restore the size above, so untouched
      // pairs take it again.
      window.addEventListener('pageshow', () => {
        buyNow?.removeAttribute('aria-busy');
        pairs.forEach((pair) => {
          if (!pair.touched) pair.size = mainSize();
        });
        renderPairs();
      });
      renderPairs();
    }

    // The theme's Buy it now, shown only while the sizes are mixed: adds every
    // pair to the cart (with anything already in it), then goes to checkout,
    // where the bundle discounts apply as they do in the cart.
    buyNow?.addEventListener('click', async () => {
      const items = mixedItems();
      if (!items || buyNow.disabled) return;
      if (errorBox) errorBox.hidden = true;
      buyNow.disabled = true;
      buyNow.setAttribute('aria-busy', 'true');
      try {
        // Through addItems, so the cart count and drawer are right if the
        // shopper comes back from checkout.
        await cart.addItems(items);
        window.location.href = '/checkout';
      } catch (error) {
        cart.refresh().catch(() => {});
        buyNow.removeAttribute('aria-busy');
        buyNow.disabled = false;
        if (errorBox) {
          errorBox.textContent = error.message || strings.cartError;
          errorBox.hidden = false;
        }
      }
    });

    const showMedia = (mediaId) => {
      if (!mediaId) return;
      $$('.product__slide', section).forEach((slide) => {
        const on = slide.dataset.mediaId === String(mediaId);
        slide.classList.toggle('is-active', on);
        slide.hidden = !on;
      });
      $$('[data-thumb]', section).forEach((thumb) => {
        if (thumb.dataset.thumb === String(mediaId)) thumb.setAttribute('aria-current', 'true');
        else thumb.removeAttribute('aria-current');
      });
    };

    const markAvailability = (selected) => {
      fieldsets.forEach((fs, index) => {
        $$('input[data-option]', fs).forEach((input) => {
          const probe = selected.slice();
          probe[index] = input.value;
          const ok = variants.some(
            (v) => v.available && v.options.every((o, i) => i === index || o === probe[i]) && v.options[index] === input.value
          );
          input.nextElementSibling?.classList.toggle('is-unavailable', !ok);
        });
      });
    };

    const update = () => {
      const selected = selectedOptions();
      const variant = variants.find((v) => v.options.every((o, i) => o === selected[i]));
      markAvailability(selected);
      if (!variant) {
        if (addBtn) addBtn.disabled = true;
        if (addLabel) addLabel.textContent = strings.unavailable;
        return;
      }
      if (idInput) idInput.value = variant.id;
      if (priceNow) priceNow.textContent = variant.price;
      if (priceWas) {
        priceWas.hidden = !variant.compare_at_price;
        priceWas.textContent = variant.compare_at_price || '';
      }
      if (addBtn) addBtn.disabled = !variant.available;
      if (addLabel) addLabel.textContent = variant.available ? strings.addToCart : strings.soldOut;
      const stockLine = $('[data-stock-line]', section);
      if (stockLine) {
        const left = variant.stock_left;
        stockLine.hidden = !(left > 0 && left <= 10);
        if (!stockLine.hidden) stockLine.textContent = strings.stockLow.replace('[left]', left).replace('[size]', variant.options[0]);
      }
      updateTotal(variant);
      renderPairs();
      showMedia(variant.media_id);
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url.toString());
    };

    fieldsets.forEach((fs) => fs.addEventListener('change', update));
    if (fieldsets.length) markAvailability(selectedOptions());

    const thumbs = $$('[data-thumb]', section);
    thumbs.forEach((thumb) => thumb.addEventListener('click', () => showMedia(thumb.dataset.thumb)));

    // On a phone, a sideways swipe on the main photo shows the next or previous one.
    const stage = $('[data-gallery-stage]', section);
    if (stage && thumbs.length > 1) {
      let startX = null;
      let startY = 0;
      stage.addEventListener(
        'touchstart',
        (event) => {
          startX = event.touches[0].clientX;
          startY = event.touches[0].clientY;
        },
        { passive: true }
      );
      stage.addEventListener(
        'touchend',
        (event) => {
          if (startX === null) return;
          const dx = event.changedTouches[0].clientX - startX;
          const dy = event.changedTouches[0].clientY - startY;
          startX = null;
          if (Math.abs(dx) < 40 || Math.abs(dx) < Math.abs(dy) * 1.5) return;
          const ids = thumbs.map((thumb) => thumb.dataset.thumb);
          const current = ids.indexOf($('.product__slide.is-active', section)?.dataset.mediaId);
          const next = (current + (dx < 0 ? 1 : -1) + ids.length) % ids.length;
          showMedia(ids[next]);
          const strip = thumbs[next].closest('.product__thumbs');
          const li = thumbs[next].parentElement;
          if (strip && li) {
            const left = li.offsetLeft - strip.offsetLeft;
            if (left < strip.scrollLeft || left + li.offsetWidth > strip.scrollLeft + strip.clientWidth) {
              strip.scrollTo({ left: left - (strip.clientWidth - li.offsetWidth) / 2, behavior: 'smooth' });
            }
          }
        },
        { passive: true }
      );
    }

    $$('[data-qty-step]', section).forEach((btn) =>
      btn.addEventListener('click', () => {
        const field = $('[data-qty-field]', section);
        if (!field) return;
        field.value = Math.max(1, Number(field.value || 1) + Number(btn.dataset.qtyStep));
      })
    );

    if (form && (VS.cartType === 'drawer' || pairBox)) {
      form.addEventListener('submit', async (event) => {
        const items = mixedItems();
        // Cart page mode: the form posts as it always has, unless the pairs
        // have different sizes, which a plain form post can't send.
        if (VS.cartType !== 'drawer' && !items) return;
        event.preventDefault();
        if (!addBtn || addBtn.disabled) return;
        if (errorBox) errorBox.hidden = true;
        addBtn.setAttribute('aria-busy', 'true');
        addBtn.disabled = true;
        if (addLabel) addLabel.textContent = strings.adding;
        try {
          syncQty();
          if (items) {
            await cart.addItems(items);
          } else {
            const body = new FormData(form);
            body.delete('bundle_quantity');
            Array.from(body.keys())
              .filter((key) => key.startsWith('pair_size_'))
              .forEach((key) => body.delete(key));
            await cart.add(body);
          }
          if (addLabel) addLabel.textContent = strings.added;
          // The phone bar's button submits this form too; focus returns to the
          // button that was pressed when the drawer closes.
          cart.open(event.submitter || addBtn);
        } catch (error) {
          cart.refresh().catch(() => {});
          if (errorBox) {
            errorBox.textContent = error.message || strings.cartError;
            errorBox.hidden = false;
            // Sent from the phone bar: bring the message into view.
            if (event.submitter && event.submitter !== addBtn) errorBox.scrollIntoView({ block: 'center' });
          }
        } finally {
          addBtn.removeAttribute('aria-busy');
          window.setTimeout(() => {
            addBtn.disabled = false;
            if (addLabel) addLabel.textContent = strings.addToCart;
            update();
          }, 1200);
        }
      });
    }

    // Phone bar (up to 989px, in CSS): shown while the form's own add button is
    // off screen. Its size buttons tick the form's own radios and its add button
    // submits the form (form attribute), so the cart gets the chosen size and
    // bundle through the one submit handler above: one add to cart per tap.
    const bar = $('[data-sticky-buy]', section);
    if (bar && addBtn && form && 'IntersectionObserver' in window) {
      const barAdd = $('[data-sticky-add]', bar);
      const barLabel = $('[data-sticky-label]', bar);
      const barTotal = $('[data-sticky-total]', bar);
      const sizeButtons = $$('[data-sticky-size]', bar);
      const sizeInputs = fieldsets[0] ? $$('input[data-option]', fieldsets[0]) : [];
      const mirror = () => {
        if (barLabel) barLabel.textContent = (addLabel || addBtn).textContent.trim();
        // Mixed sizes: the size buttons step aside (CSS) and the line lists them.
        const mixed = isMixed();
        bar.classList.toggle('is-mixed', mixed);
        if (barTotal) {
          // "2 pairs · R 500.00": the chosen bundle's own line, so the total
          // reads right away from the bundle cards. Mixed: "3 pairs: M, M, L · R 675.00".
          const picked = bundleInputs.find((input) => input.checked);
          let packLine = picked?.closest('.bundle__option')?.querySelector('.bundle__name span')?.textContent.trim();
          if (mixed && packLine) packLine = `${packLine}: ${shownPairs().map((pair) => pair.size).join(', ')}`;
          const total = addTotal ? addTotal.textContent.replace(/^[\s·]+/, '').trim() : '';
          barTotal.textContent = total ? [packLine, total].filter(Boolean).join(' · ') : '';
        }
        if (barAdd) {
          barAdd.disabled = addBtn.disabled;
          if (addBtn.hasAttribute('aria-busy')) barAdd.setAttribute('aria-busy', 'true');
          else barAdd.removeAttribute('aria-busy');
        }
        sizeButtons.forEach((button) => {
          const input = sizeInputs.find((i) => i.value === button.dataset.stickySize);
          button.setAttribute('aria-pressed', input?.checked ? 'true' : 'false');
          button.classList.toggle('is-unavailable', !!input?.nextElementSibling?.classList.contains('is-unavailable'));
        });
      };
      sizeButtons.forEach((button) =>
        button.addEventListener('click', () => {
          const input = sizeInputs.find((i) => i.value === button.dataset.stickySize);
          if (!input || input.checked) return;
          input.checked = true;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        })
      );
      fieldsets.forEach((fs) => fs.addEventListener('change', mirror));
      bundleInputs.forEach((input) => input.addEventListener('change', mirror));
      section.addEventListener('vs:pairs', mirror);
      new MutationObserver(mirror).observe(addBtn, {
        subtree: true,
        childList: true,
        characterData: true,
        attributes: true,
        attributeFilter: ['disabled', 'aria-busy'],
      });
      mirror();
      bar.hidden = false;
      document.documentElement.classList.add('has-sticky-buy');
      // Shown while the form's own add button is off screen, except while any
      // part of the buy area (the price row down to the note under the form)
      // sits at the bottom of the screen, where the bar would cover it.
      const buyTop = $('.product__price-row', section) || form;
      const buyEnd = $('.product__buy-note', section) || form;
      let addInView = true;
      const place = () => {
        const top = buyTop.getBoundingClientRect().top;
        const bottom = buyEnd.getBoundingClientRect().bottom;
        const underBar = top < window.innerHeight && bottom > window.innerHeight - bar.offsetHeight;
        bar.classList.toggle('is-visible', !addInView && !underBar);
      };
      new IntersectionObserver(([entry]) => {
        addInView = entry.isIntersecting;
        place();
      }).observe(addBtn);
      window.addEventListener('scroll', place, { passive: true });
      window.addEventListener('resize', place);
    }
  }

  /* ---------------------------------------------------------------------
     Fit finder
     --------------------------------------------------------------------- */
  function initFitFinder(root) {
    if (root.dataset.ready) return;
    root.dataset.ready = 'true';
    const rows = $$('tr[data-size]', root).map((tr) => ({
      tr,
      size: tr.dataset.size,
      aMin: Number(tr.dataset.ankleMin),
      aMax: Number(tr.dataset.ankleMax),
      cMin: Number(tr.dataset.calfMin),
      cMax: Number(tr.dataset.calfMax),
    }));
    const ankle = $('[data-fit-ankle]', root);
    const calf = $('[data-fit-calf]', root);
    const result = $('[data-fit-result]', root);
    const sizeEl = $('[data-fit-size]', root);
    const noteEl = $('[data-fit-note]', root);
    const contactEl = $('[data-fit-contact]', root);
    let text = {};
    try {
      text = JSON.parse($('[data-fit-strings]', root)?.innerHTML || '{}');
    } catch (e) {
      text = {};
    }

    // Locale strings from data-fit-strings; "[size]" is swapped for the size label.
    const say = (key, size = '') => String(text[key] || '').replace(/\[size\]/g, () => size);

    const run = () => {
      const a = parseFloat(ankle?.value);
      const c = parseFloat(calf?.value);
      rows.forEach((r) => r.tr.classList.remove('is-match'));
      result?.classList.remove('is-match');
      if (contactEl) contactEl.hidden = true;
      // The ankle decides the size; the calf is an optional check.
      if (Number.isNaN(a)) {
        sizeEl.textContent = '';
        noteEl.textContent = say('prompt');
        return;
      }
      // The chart is in whole centimetres: 19–22 covers 19 up to just under 23.
      const inRange = (v, min, max) => v >= min && v < max + 1;
      const fit = rows.find((r) => inRange(a, r.aMin, r.aMax));
      if (!fit) {
        sizeEl.textContent = '?';
        noteEl.textContent = say('noMatch');
        if (contactEl) contactEl.hidden = false;
        return;
      }
      sizeEl.textContent = fit.size;
      fit.tr.classList.add('is-match');
      result?.classList.add('is-match');
      const calfOutside = !Number.isNaN(c) && !inRange(c, fit.cMin, fit.cMax);
      noteEl.textContent = say(calfOutside ? 'calfOutside' : 'match', fit.size);
      if (calfOutside && contactEl) contactEl.hidden = false;
    };
    [ankle, calf].forEach((input) => input?.addEventListener('input', run));
    $('[data-fit-form]', root)?.addEventListener('submit', (e) => e.preventDefault());
  }

  /* ---------------------------------------------------------------------
     Collection sorting
     --------------------------------------------------------------------- */
  document.addEventListener('change', (event) => {
    const select = event.target.closest('[data-sort-select]');
    if (select) select.form.submit();
  });

  /* ---------------------------------------------------------------------
     Cart page: the drawer's -/+ stepper. Each change submits the cart form
     (Shopify's own update), so totals come back from the server. A short
     wait lets several clicks land as one update.
     --------------------------------------------------------------------- */
  let cartPageTimer;
  const submitCartPage = (form, line) => {
    clearTimeout(cartPageTimer);
    line?.setAttribute('aria-busy', 'true');
    cartPageTimer = setTimeout(() => {
      const update = $('[data-cart-update]', form);
      if (form.requestSubmit && update) form.requestSubmit(update);
      else if (update) update.click();
      else form.submit();
    }, 400);
  };
  document.addEventListener('click', (event) => {
    const step = event.target.closest('[data-cart-step]');
    const form = step?.closest('[data-cart-page-form]');
    const input = step && $('[data-cart-qty]', step.closest('.qty'));
    if (!form || !input) return;
    input.value = Math.max(0, Number(input.value || 0) + Number(step.dataset.cartStep));
    submitCartPage(form, step.closest('.cart-line'));
  });
  document.addEventListener('change', (event) => {
    const input = event.target.closest('[data-cart-qty]');
    const form = input?.closest('[data-cart-page-form]');
    if (form) submitCartPage(form, input.closest('.cart-line'));
  });

  /* ---------------------------------------------------------------------
     Product recommendations
     --------------------------------------------------------------------- */
  if (!customElements.get('product-recommendations')) {
    customElements.define(
      'product-recommendations',
      class extends HTMLElement {
        connectedCallback() {
          if (this.querySelector('.product-grid') || !this.dataset.url) return;
          const observer = new IntersectionObserver(
            (entries) => {
              if (!entries[0].isIntersecting) return;
              observer.disconnect();
              fetch(this.dataset.url)
                .then((r) => r.text())
                .then((html) => {
                  const doc = new DOMParser().parseFromString(html, 'text/html');
                  const fresh = doc.querySelector('product-recommendations');
                  if (fresh && fresh.innerHTML.trim()) {
                    this.innerHTML = fresh.innerHTML;
                    document.dispatchEvent(new CustomEvent('vs:content-added', { detail: { root: this } }));
                  }
                })
                .catch(() => {});
            },
            { rootMargin: '0px 0px 400px 0px' }
          );
          observer.observe(this);
        }
      }
    );
  }

  /* ---------------------------------------------------------------------
     Shopify Forms email teaser (app embed)
     The app draws its teaser and its popup inside an open shadow root on
     <shopify-forms-embed>, which it adds to <body> after a delay. The theme
     only reads which step is showing (the small teaser or the open form) and
     copies it to the host as data-vs-step, so base.css can lift the teaser
     above the phone buy bar and let it step aside while a [data-teaser-clear]
     area (the hero's copy and call to action, the product form) sits at the
     bottom of the screen. The app's own markup, styles and settings are left
     alone; if any of this changes, the teaser simply behaves as the app sets.
     --------------------------------------------------------------------- */
  const watchedForms = new WeakSet();
  const watchFormsEmbed = (host, tries = 0) => {
    if (watchedForms.has(host)) return;
    if (!host.shadowRoot) {
      if (tries < 5) window.setTimeout(() => watchFormsEmbed(host, tries + 1), 1000);
      return;
    }
    watchedForms.add(host);
    const sync = () => {
      const step = host.shadowRoot.querySelector('[data-current-step]')?.getAttribute('data-current-step');
      if (step && host.dataset.vsStep !== step) host.dataset.vsStep = step;
      else if (!step && host.hasAttribute('data-vs-step')) host.removeAttribute('data-vs-step');
    };
    new MutationObserver(sync).observe(host.shadowRoot, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['data-current-step'],
    });
    sync();
  };
  if (window.customElements) {
    customElements.whenDefined('shopify-forms-embed').then(() => {
      const scan = () => $$('shopify-forms-embed').forEach((host) => watchFormsEmbed(host));
      scan();
      new MutationObserver(scan).observe(document.body, { childList: true });
    });
  }

  // The bottom quarter of the screen, where the teaser floats on phones.
  const teaserBand = new Set();
  const teaserObserver =
    'IntersectionObserver' in window &&
    new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => (entry.isIntersecting ? teaserBand.add(entry.target) : teaserBand.delete(entry.target)));
        document.documentElement.classList.toggle('teaser-clear', teaserBand.size > 0);
      },
      { rootMargin: '-75% 0px 0px 0px' }
    );

  /* ---------------------------------------------------------------------
     Boot + theme editor support
     --------------------------------------------------------------------- */
  const init = (root = document) => {
    $$('[data-product-section]', root).forEach(initProduct);
    $$('[data-fit-finder]', root).forEach(initFitFinder);
    if (teaserObserver) $$('[data-teaser-clear]', root).forEach((el) => teaserObserver.observe(el));
    setDispatch(root);
    syncCartEmail(root);
  };
  init();
  document.addEventListener('shopify:section:load', (event) => init(event.target));
  // A page left open past midnight moves on to the next day's line.
  document.addEventListener('visibilitychange', () => document.visibilityState === 'visible' && setDispatch());
  window.setInterval(() => setDispatch(), 60000);
})();
